import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Box,
} from "@mui/material";
import API_BASE_URL from "../utilities/ApiConfig";
import ApiLoader from "../components/ApiLoader";
import { toast } from "react-toastify";
import { PDFDocument } from "pdf-lib";
import { Buffer } from "buffer";

const inputTypes = [
  "Sha256HashPKCS7",
  "TextPKCS7",
  "Sha256HashPKCS1",
  "TextPKCS1",
  "TextPKCS7ATTACHED",
];

const DSCSignPopup = ({ open, onClose, onSubmit, initialDataToSign, pde }) => {
  const [form, setForm] = useState({
    token: "",
    certificate: "",
    password: "",
    pdfSigning: false,
    dataToSign: "",
    dataType: "TextPKCS7",
    fileName: "",
  });

  // helper to clear everything
  const resetForm = () => {
    setForm({
      token: "",
      certificate: "",
      password: "",
      pdfSigning: false,
      dataToSign: "",
      dataType: "TextPKCS7",
      fileName: "",
    });
  };
  const [tokenList, setTokenList] = useState([]);
  const [certList, setCertList] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [loading, setLoading] = useState(false);

  // **NEW**: seed incoming Base64 into the form
  useEffect(() => {
    if (initialDataToSign) {
      setForm((f) => ({
        ...f,
        dataToSign: initialDataToSign,
        // fileName: `doc_${Date.now()}.pdf`,
        fileName: pde ? `${pde}.pdf` : `doc_${Date.now()}.pdf`,
      }));
    }
  }, [initialDataToSign, pde]);

  const fetchTokens = useCallback(async () => {
    setLoadingTokens(true);
    try {
      // 1️⃣ Build the raw request
      const rawRequest = {
        AppID: Math.random().toString().slice(2, 12),
        tokenStatus: "CONNECTED",
        tokenType: "ALL",
      };

      // 2️⃣ Encrypt via backend
      const encryptResp = await fetch(`${API_BASE_URL}/DSC/Encrypt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Type: "ListToken",
          Data: JSON.stringify(rawRequest),
        }),
      });
      const { encryptedRequest, encryptionKeyId, tempFilePath } =
        await encryptResp.json();

      if (!encryptedRequest || !encryptionKeyId) {
        throw new Error("Encryption failed or incomplete response.");
      }

      // 3️⃣ Call emBridge local service
      const bridgeResp = await fetch(
        "https://localhost.emudhra.com:26769/DSC/ListToken",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            encryptedRequest,
            encryptionKeyId,
            tempFilePath,
          }),
        }
      );
      const { responseData } = await bridgeResp.json();

      // 4️⃣ Decrypt via backend
      const decryptResp = await fetch(`${API_BASE_URL}/DSC/Decrypt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Type: "ListToken",
          Data: responseData,
          TempFilePath: tempFilePath,
        }),
      });
      const finalData = await decryptResp.json();
      setTokenList(finalData.tokens || []);
    } catch (err) {
      console.error("fetchTokens error:", err);
    } finally {
      setLoadingTokens(false);
    }
  }, []);

  const fetchCertificates = useCallback(async (selectedToken) => {
    setLoadingTokens(true);
    try {
      // 1️⃣ Build the raw request
      const rawRequest = {
        AppID: Math.random().toString().slice(2, 12),
        keyStoreDisplayName: selectedToken,
        certFilter: {
          commonName: "",
          issuerName: "",
          serialNumber: "",
          isNotExpired: "",
        },
      };

      // 2️⃣ Encrypt via backend
      const encryptResp = await fetch(`${API_BASE_URL}/DSC/Encrypt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Type: "ListCertificate",
          Data: JSON.stringify(rawRequest),
        }),
      });
      const { encryptedRequest, encryptionKeyId, tempFilePath } =
        await encryptResp.json();

      if (!encryptedRequest || !encryptionKeyId) {
        throw new Error("Encryption failed or incomplete response.");
      }

      // 3️⃣ Call emBridge local service
      const bridgeResp = await fetch(
        "https://localhost.emudhra.com:26769/DSC/ListCertificate",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            encryptedRequest,
            encryptionKeyId,
            tempFilePath,
          }),
        }
      );
      const { responseData } = await bridgeResp.json();

      // 4️⃣ Decrypt via backend
      const decryptResp = await fetch(`${API_BASE_URL}/DSC/Decrypt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Type: "ListCertificate",
          Data: responseData,
          TempFilePath: tempFilePath,
        }),
      });
      const finalData = await decryptResp.json();
      console.log("Decrypted certificate data:", finalData);
      setCertList(finalData.Certificates || []);
    } catch (err) {
      console.error("fetchCertificates error:", err);
    } finally {
      setLoadingTokens(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchTokens();
  }, [open, fetchTokens]);

  useEffect(() => {
    if (form.token) fetchCertificates(form.token);
  }, [form.token, fetchCertificates]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      setForm((f) => ({ ...f, dataToSign: base64, fileName: file.name }));
    };
    reader.readAsDataURL(file);
  };

  //-------------------------------------------------------bulk sign starts-------------------------------------------------------------------------------------

  const handleSubmit = async () => {
    try {
      if (!form.dataToSign) {
        toast.warning("Please select a PDF file to sign.");
        return;
      }
      setLoading(true);

      const raw = form.dataToSign.split(",")[1] || form.dataToSign;
      const validFrom = new Date(certList[0].ValidFrom)
        .toISOString()
        .split("T")[0];
      const validTill = new Date(certList[0].ValidTill)
        .toISOString()
        .split("T")[0];

      debugger;
      // 2️⃣ build the envelope your C# expects
      const envelope = {
        Type: "PKCSBulkSign",
        Data: JSON.stringify({
          KeyStoreDisplayName: form.token,
          KeyStorePassphrase: form.password,
          KeyId: form.certificate,
          BulkInput: [
            {
              DocBase64: raw, // → docBase64
              DocId: form.fileName || `doc-${Date.now()}`,
              Location: form.location || "Bengaluru",
              Reason: form.reason || "Approval",
              SignedBy: form.contact || "eMudhra Ltd.",
              CoSign: false,
              Page: "All", // enum name of PageTobeSigned
              Coordinates: "BottomRight", // enum name of Coordinates
              // AppearanceText: `Digitally Signed By\n${certList[0].CommonName}\nValid From ${certList[0].ValidFrom}\nValid Till ${certList[0].ValidTill}`,
              AppearanceText: `Digitally Signed By\n${certList[0].CommonName}\nValid From ${validFrom}\nValid Till ${validTill}`,
              IsAcro6Layer: false,
            },
          ],
        }),
      };
      debugger;
      // 2️⃣ Send to your Encrypt endpoint
      const resp = await fetch(`${API_BASE_URL}/DSC/Encrypt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(envelope),
      });
      const enc = await resp.json();
      // if (!enc.encryptedRequest)
      //   throw new Error(enc.ErrorMsg || "Encryption failed");
      if (!enc.encryptedRequest) {
        toast.error(enc.ErrorMsg || "Encryption failed");
        return;
      }
      console.log("✅ got encrypted payload:", enc);

      // 3️⃣ Call emBridge’s bulk-sign endpoint
      const bridgeResp = await fetch(
        "https://localhost.emudhra.com:26769/DSC/PKCSBulkSign",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            encryptedRequest: enc.encryptedRequest,
            encryptionKeyId: enc.encryptionKeyId,
            tempFilePath: enc.tempFilePath || null,
          }),
        }
      );
      const bridgeData = await bridgeResp.json();
      // if (bridgeData.status !== 1) {
      //   throw new Error(bridgeData.errorMessage || "Bulk sign failed.");
      // }

      if (bridgeData.status !== 1) {
        toast.error(bridgeData.errorMessage || "Bulk sign failed.");
        return;
      }

      // 4️⃣ Decrypt the signed blob via your local API
      const decryptResp = await fetch(`${API_BASE_URL}/DSC/Decrypt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Type: "PKCSBulkSign",
          Data: bridgeData.responseData,
          TempFilePath: enc.tempFilePath || null,
        }),
      });
      const finalResult = await decryptResp.json();

      // 5️⃣ Pull out the first signed PDF
      const items = finalResult.bulkSignItems;
      if (!Array.isArray(items) || items.length === 0) {
        // throw new Error("No signed documents returned.");
        toast.error("No signed documents returned.");
        return;
      }
      const { signedData: b64, id: filename } = items[0];
      if (!b64) {
        // throw new Error("signedData is empty");
        toast.error("signedData is empty");
        return;
      }

      const saveResp = await fetch(`${API_BASE_URL}/DSCMail/SaveSignedDoc`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          FileName: filename,
          Base64Data: b64,
        }),
      });
      const saveResult = await saveResp.json();
      // if (!saveResp.ok) {
      //   throw new Error(
      //     saveResult.error || "Failed to save signed PDF on server."
      //   );
      // }
      if (!saveResp.ok) {
        toast.error(saveResult.error || "Failed to save signed PDF on server.");
        return;
      }
      console.log("✅ Server saved:", saveResult);

      // 6️⃣ Convert base64 → Blob → trigger desktop download
      const binary = atob(b64.replace(/\s+/g, ""));
      const len = binary.length;
      const buffer = new ArrayBuffer(len);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < len; i++) {
        view[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([view], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      // create a hidden <a download> and click it
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "signed.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // free up the URL after a bit
      setTimeout(() => URL.revokeObjectURL(url), 60_000);

      // 7️⃣ Close your dialog
      onClose?.();
      resetForm();
      toast.success("PDF signed and downloaded successfully!");
    } catch (err) {
      console.error("Signing error:", err);
      toast.error(`Error during PDF signing: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // const handleSubmit = async () => {
  //   try {
  //     if (!form.dataToSign) {
  //       toast.warning("Please select a PDF file to sign.");
  //       return;
  //     }
  //     setLoading(true);

  //     // 🔹 1) normalize Base64
  //     const raw = form.dataToSign.split(",")[1] || form.dataToSign;
  //     const baseId = form.fileName || `doc-${Date.now()}`;

  //     // 🔹 2) measure page size (first page—assumes uniform size)
  //     const bytes = Uint8Array.from(atob(raw), (c) => c.charCodeAt(0));
  //     const pdfDoc = await PDFDocument.load(bytes);
  //     const { width, height } = pdfDoc.getPages()[0].getSize();

  //     // 🔹 3) compute coords
  //     const sigW = 150,
  //       sigH = 75;
  //     const margin = 20;
  //     const bottomX = width - sigW - margin;
  //     const bottomY = margin;
  //     const centerX = width - sigW - margin;
  //     const centerY = height / 2 - sigH / 2 - margin;

  //     // 🔹 4) build TWO BulkInput entries
  //     const validFrom = new Date(certList[0].ValidFrom)
  //       .toISOString()
  //       .split("T")[0];
  //     const validTill = new Date(certList[0].ValidTill)
  //       .toISOString()
  //       .split("T")[0];

  //     const envelope = {
  //       Type: "PKCSBulkSign",
  //       Data: JSON.stringify({
  //         KeyStoreDisplayName: form.token,
  //         KeyStorePassphrase: form.password,
  //         KeyId: form.certificate,
  //         BulkInput: [
  //           // a) bottom-right on all pages
  //           {
  //             DocBase64: raw,
  //             DocId: baseId,
  //             Location: form.location || "Bengaluru",
  //             Reason: form.reason || "Approval",
  //             SignedBy: form.contact || "eMudhra Ltd.",
  //             CoSign: false,
  //             Page: "All",
  //             Coordinates: "BottomRight",
  //             AppearanceText:
  //               `Digitally Signed By\n${certList[0].CommonName}\n` +
  //               `Valid From ${validFrom}\nValid Till ${validTill}`,
  //             IsAcro6Layer: false,
  //           },
  //           // b) free-form on last page
  //           {
  //             DocBase64: raw,
  //             DocId: `${baseId}_last`,
  //             Location: form.location || "Bengaluru",
  //             Reason: form.reason || "Approval",
  //             SignedBy: form.contact || "eMudhra Ltd.",
  //             CoSign: false,
  //             Page: "Last",
  //             CoordinateX: Math.round(centerX),
  //             CoordinateY: Math.round(centerY),
  //             AppearanceText:
  //               `Digitally Signed By\n${certList[0].CommonName}\n` +
  //               `Valid From ${validFrom}\nValid Till ${validTill}`,
  //             IsAcro6Layer: false,
  //           },
  //         ],
  //       }),
  //     };

  //     // 🔹 5) encrypt → bridge → decrypt
  //     const encRes = await fetch(`${API_BASE_URL}/DSC/Encrypt`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(envelope),
  //     });
  //     if (!encRes.ok) throw new Error(`Encrypt HTTP ${encRes.status}`);
  //     const enc = await encRes.json();
  //     if (!enc.encryptedRequest)
  //       throw new Error(enc.ErrorMsg || "Encryption failed");

  //     const bridge = await (
  //       await fetch("https://localhost.emudhra.com:26769/DSC/PKCSBulkSign", {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify({
  //           encryptedRequest: enc.encryptedRequest,
  //           encryptionKeyId: enc.encryptionKeyId,
  //           tempFilePath: enc.tempFilePath || null,
  //         }),
  //       })
  //     ).json();
  //     if (bridge.status !== 1)
  //       throw new Error(bridge.errorMessage || "Bulk sign failed");

  //     const dec = await (
  //       await fetch(`${API_BASE_URL}/DSC/Decrypt`, {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify({
  //           Type: "PKCSBulkSign",
  //           Data: bridge.responseData,
  //           TempFilePath: enc.tempFilePath,
  //         }),
  //       })
  //     ).json();

  //     const item = dec.bulkSignItems?.[0];
  //     if (!item?.signedData) throw new Error("No signed data returned");

  //     // 🔹 6) save & download
  //     await fetch(`${API_BASE_URL}/DSCMail/SaveSignedDoc`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({
  //         FileName: item.id,
  //         Base64Data: item.signedData,
  //       }),
  //     });
  //     const bin = atob(item.signedData.replace(/\s+/g, ""));
  //     const u8b = new Uint8Array(bin.length);
  //     for (let i = 0; i < bin.length; i++) u8b[i] = bin.charCodeAt(i);
  //     const blob = new Blob([u8b], { type: "application/pdf" });
  //     const url = URL.createObjectURL(blob);
  //     const a = document.createElement("a");
  //     a.href = url;
  //     a.download = item.id;
  //     document.body.appendChild(a);
  //     a.click();
  //     document.body.removeChild(a);
  //     setTimeout(() => URL.revokeObjectURL(url), 60_000);

  //     onClose?.();
  //     resetForm();
  //     toast.success("PDF signed and downloaded successfully!");
  //   } catch (err) {
  //     console.error(err);
  //     toast.error(`Error: ${err.message}`);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  //-------------------------------------------------------bulk sign ends-------------------------------------------------------------------------------------

  return (
    <>
      {loading && <ApiLoader />}
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>DSC Signing</DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            {loadingTokens ? (
              <CircularProgress size={24} />
            ) : (
              <TextField
                select
                fullWidth
                label="Tokens"
                name="token"
                value={form.token}
                onChange={handleChange}
              >
                {tokenList.map((t) => (
                  <MenuItem
                    key={t.keyStoreDisplayName}
                    value={t.keyStoreDisplayName}
                  >
                    {t.keyStoreDisplayName}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Button variant="outlined" onClick={fetchTokens}>
              Refresh
            </Button>
          </Box>

          <TextField
            select
            fullWidth
            margin="dense"
            label="Certificates"
            name="certificate"
            value={form.certificate}
            onChange={handleChange}
          >
            {certList.map((c) => (
              <MenuItem key={c.KeyId} value={c.KeyId}>
                {c.CommonName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            margin="dense"
            label="Password"
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
          />

          {/* file-input remains but is no longer required when using initialDataToSign */}
          <Box mt={2} mb={2}>
            <input
              accept="application/pdf"
              id="pdf-input"
              type="file"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!form.dataToSign}
          >
            Sign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DSCSignPopup;
